'use client';

import { useState, useEffect, useRef } from 'react';
import { commonClient, type CurrencyItem } from '@bip/api-client';
import { getGeolocation } from '@/_lib/getGeolocation';

const COOKIE_NAME = 'tp_currency';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(';').shift()!.trim() || null;
  return null;
}

function setCookie(name: string, value: string) {
  document.cookie = `${name}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function CurrencyDropdown() {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [topCurrencies, setTopCurrencies] = useState<CurrencyItem[]>([]);
  const [allCurrencies, setAllCurrencies] = useState<CurrencyItem[]>([]);
  // Root is a `<div>` (was `<li>`) so this component sits alongside
  // the desktop `<ul>` in the new flex header layout rather than
  // inside it. Ref type updated to match.
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      let currencies: CurrencyItem[] = [];
      try {
        const res = await commonClient.getCurrencyList();
        if (res?.message === 'success' && res.data) {
          currencies = res.data;
        }
      } catch {
        // API unavailable — will show default GBP
      }

      if (cancelled) return;

      // Deduplicate by CurrencyCode
      const seen = new Set<string>();
      const top: CurrencyItem[] = [];
      const rest: CurrencyItem[] = [];
      for (const c of currencies) {
        if (seen.has(c.CurrencyCode)) continue;
        seen.add(c.CurrencyCode);
        (c.IsMain ? top : rest).push(c);
      }
      setTopCurrencies(top);
      setAllCurrencies(rest);

      const allList = [...top, ...rest];

      // 2. Check cookie first
      const cookieCurrency = getCookie(COOKIE_NAME);

      if (cookieCurrency && !cookieCurrency.includes('null')) {
        // Cookie exists — use it
        const match = allList.find((c) => c.CurrencyCode === cookieCurrency);
        if (match) {
          setLabel(`${match.CurrencySymbol} ${match.CurrencyCode}`);
          setIsReady(true);
          return;
        }
      }

      // 3. No valid cookie — detect by geolocation
      try {
        const geoData = await getGeolocation();
        if (cancelled) return;

        const userCountry = geoData?.country ?? 'United Kingdom';

        try {
          const currencyRes = await commonClient.getCurrencyByCountry(userCountry);
          if (cancelled) return;

          if (currencyRes?.message === 'success' && currencyRes.data) {
            const detected = currencyRes.data;
            setCookie(COOKIE_NAME, detected.CurrencyCode);
            setLabel(`${detected.CurrencySymbol} ${detected.CurrencyCode}`);
            setIsReady(true);
            // We only reach geo-detection when there was NO valid `tp_currency` cookie, so the
            // server-seeded CurrencyProvider (and every price on the page) defaulted to GBP. If geo
            // resolves a DIFFERENT currency, reload once so the SSR seed re-reads the new cookie and
            // all surfaces agree — filter, cards, detail, and the backend `userCurrency`. Without
            // this, the provider stays on GBP (it never re-reads once seeded) while the cookie/backend
            // move to the detected currency, so cards render in GBP while the filter shows the new
            // currency. Same one-shot reload the manual currency switch does; the cookie guard makes
            // it loop-safe (after reload the cookie exists → init() takes the cookie branch above).
            if (
              detected.CurrencyCode !== 'GBP' &&
              getCookie(COOKIE_NAME) === detected.CurrencyCode
            ) {
              window.location.reload();
            }
            return;
          }
        } catch {
          // Country lookup failed — fall through to default
        }
      } catch {
        // Geo API failed — fall through to default
      }

      // 4. Fallback to GBP
      const gbp = allList.find((c) => c.CurrencyCode === 'GBP');
      if (gbp) {
        setCookie(COOKIE_NAME, 'GBP');
        setLabel(`${gbp.CurrencySymbol} GBP`);
      } else {
        setCookie(COOKIE_NAME, 'GBP');
        setLabel('£ GBP');
      }
      setIsReady(true);
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const selectCurrency = (code: string) => {
    const match = [...topCurrencies, ...allCurrencies].find((c) => c.CurrencyCode === code);
    if (match) {
      setCookie(COOKIE_NAME, match.CurrencyCode);
      setLabel(`${match.CurrencySymbol} ${match.CurrencyCode}`);
      setOpen(false);
      window.location.reload();
    }
  };

  if (!isReady) return null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="currency"
        className="text-black font-medium flex items-center space-x-1 dropdown-toggle selectedCurrency"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{label}</span>
        {/* <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg> */}
      </button>

      {open && (
        <div className="CurrencyDropdown absolute right-0 z-50 bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[275px] dropdown-menu-right">
          <div className="currency-list max-h-80 overflow-y-auto">
            {topCurrencies.length > 0 && (
              <>
                <h6 className="dropdown-header">Top Currencies</h6>
                {topCurrencies.map((c) => (
                  <button
                    key={c.CurrencyCode}
                    className="dropdown-item"
                    onClick={() => selectCurrency(c.CurrencyCode)}
                  >
                    {c.CurrencyCode} –{' '}
                    {c.CurrencyName.length > 25
                      ? c.CurrencyName.slice(0, 25) + '…'
                      : c.CurrencyName}
                  </button>
                ))}
              </>
            )}

            {allCurrencies.length > 0 && (
              <>
                <div className="border-t border-gray-200 my-1" />
                <h6 className="dropdown-header">All Currencies</h6>
                {allCurrencies.map((c) => (
                  <button
                    key={c.CurrencyCode}
                    className="dropdown-item"
                    onClick={() => selectCurrency(c.CurrencyCode)}
                  >
                    {c.CurrencyCode} –{' '}
                    {c.CurrencyName.length > 25
                      ? c.CurrencyName.slice(0, 25) + '…'
                      : c.CurrencyName}
                  </button>
                ))}
              </>
            )}

            {topCurrencies.length === 0 && allCurrencies.length === 0 && (
              <span className="dropdown-item text-muted">Loading currencies...</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
